import { useEffect, useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'
import type { CotizacionTemplateDTO, PagaebleResult, SearchCotizacionTemplateDTO, WrapperResponse } from '../../../model/kartera-api-model'
import { useHistory } from '../../../router/history'
import APIInvoke from '../../../utils/APIInvoke'
import {
    editQuoteTemplateLocation,
    errorMessage,
    NEW_QUOTE_TEMPLATE_PATH,
    pageWindow,
    QUOTE_TEMPLATES_PATH,
    QUOTES_PATH,
} from '../common/quote-template-common'
import type { QuoteLocationState } from '../common/quote-template-common'

export const PAGE_SIZES = [20, 50, 100]
const TEXT_DELAY_MS = 400
const LOAD_ERROR = 'No se pudo cargar el listado de templates.'

export interface QuoteTemplateListFilters {
    nombre: string
    ramo: string
}

export interface QuoteTemplateRow {
    id: number
    nombre: string | null
    ramo: string | null
    aseguradora: string | null
    plan: string | null
    sumaAsegurada: string | null
    moneda: string | null
    /** Aseguradora y plan, para la vista móvil. */
    resumen: string
}

const defaultFilters: QuoteTemplateListFilters = { nombre: '', ramo: '' }

export const NEW_TEMPLATE_LOCATION: { pathname: string; state: QuoteLocationState } = {
    pathname: NEW_QUOTE_TEMPLATE_PATH,
    state: { backUrl: QUOTE_TEMPLATES_PATH },
}

const buildQuery = (filters: QuoteTemplateListFilters) => {
    const params: string[] = ['order=nombre%20asc']
    if (filters.nombre.trim()) params.push(`nombre[cn]=${encodeURIComponent(filters.nombre.trim())}`)
    if (filters.ramo) params.push(`ramo=${encodeURIComponent(filters.ramo)}`)
    return params.join('&')
}

const toRow = (item: SearchCotizacionTemplateDTO): QuoteTemplateRow => ({
    id: item.id,
    nombre: item.nombre,
    ramo: item.ramo,
    aseguradora: item.aseguradora,
    plan: item.plan,
    sumaAsegurada: item.sumaAsegurada,
    moneda: item.moneda,
    resumen: [item.aseguradora, item.plan].filter(Boolean).join(' · '),
})

export const useQuoteTemplateListModel = () => {
    const history = useHistory()
    const location = useLocation()
    const locationState = location.state as QuoteLocationState | null
    const filtersRef = useRef<QuoteTemplateListFilters>(defaultFilters)
    const textTimer = useRef<ReturnType<typeof setTimeout>>(undefined)
    const [filters, setFilters] = useState<QuoteTemplateListFilters>(defaultFilters)
    const [query, setQuery] = useState(() => buildQuery(defaultFilters))
    const [page, setPage] = useState(1)
    const [pageSize, setPageSize] = useState(PAGE_SIZES[0])
    const [reloadToken, setReloadToken] = useState(0)
    const [loading, setLoading] = useState(true)
    const [initialLoadDone, setInitialLoadDone] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const [items, setItems] = useState<SearchCotizacionTemplateDTO[]>([])
    const [totalItems, setTotalItems] = useState(0)
    const [totalPages, setTotalPages] = useState(0)
    const [detailId, setDetailId] = useState<number | null>(() => locationState?.detailId || null)

    useEffect(() => () => clearTimeout(textTimer.current), [])

    useEffect(() => {
        let active = true
        const load = async () => {
            try {
                setLoading(true)
                setError(null)
                const response = await APIInvoke.invokeGET(`/cotizaciones/templates?page=${page}&size=${pageSize}&${query}`)
                const payload: WrapperResponse<PagaebleResult<SearchCotizacionTemplateDTO>> | null = await response.json()
                if (!active) return
                if (!payload?.ok || !payload.body) throw new Error(payload?.message || LOAD_ERROR)
                setItems(payload.body.items || [])
                setTotalItems(payload.body.totalItems || 0)
                setTotalPages(payload.body.totalPages || 0)
                setInitialLoadDone(true)
            } catch (loadError) {
                if (active) setError(errorMessage(loadError, LOAD_ERROR))
            } finally {
                if (active) setLoading(false)
            }
        }
        load()
        return () => { active = false }
    }, [page, pageSize, query, reloadToken])

    const commit = (next: QuoteTemplateListFilters) => {
        clearTimeout(textTimer.current)
        filtersRef.current = next
        setFilters(next)
        setPage(1)
        setQuery(buildQuery(next))
    }

    const changeNombre = (nombre: string) => {
        const next = { ...filtersRef.current, nombre }
        filtersRef.current = next
        setFilters(next)
        clearTimeout(textTimer.current)
        textTimer.current = setTimeout(() => {
            setPage(1)
            setQuery(buildQuery(filtersRef.current))
        }, TEXT_DELAY_MS)
    }

    const changePageSize = (value: number) => {
        setPageSize(Number(value))
        setPage(1)
    }

    const deleted = (template?: CotizacionTemplateDTO | null) => {
        const id = template?.id ?? detailId
        setItems((current) => current.filter((item) => item.id !== id))
        setTotalItems((current) => Math.max(0, current - 1))
        setDetailId(null)
    }

    const rangeFrom = totalItems === 0 ? 0 : ((page - 1) * pageSize) + 1

    return {
        filters,
        page,
        pageSize,
        loading,
        initialLoadDone,
        error,
        rows: items.map(toRow),
        totalItems,
        totalPages,
        pages: totalPages > 0 ? pageWindow(page, totalPages) : [],
        rangeFrom,
        rangeTo: totalItems === 0 ? 0 : rangeFrom + items.length - 1,
        detailId,
        backTo: locationState?.backUrl && locationState.backUrl !== QUOTE_TEMPLATES_PATH ? locationState.backUrl : QUOTES_PATH,
        changeNombre,
        submitNombre: () => commit(filtersRef.current),
        changeRamo: (ramo: string) => commit({ ...filtersRef.current, ramo }),
        clearFilters: () => commit({ ...defaultFilters }),
        setPage,
        changePageSize,
        reload: () => setReloadToken((token) => token + 1),
        openTemplate: (id: number) => setDetailId(id),
        closeTemplate: () => setDetailId(null),
        editTemplate: (id: number) => history.push(editQuoteTemplateLocation(id)),
        deleted,
    }
}
